import { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Paper } from '@mui/material';
import { useForm, FieldErrors, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';

import { useAppDispatch } from '../../../app/store/configureStore';
import FormButtons from '../../../app/components/FormButtons';
import { createKitchenAsync } from './createKitchenSlice';
import { updateKitchenAsync } from './updateKitchenSlice';
import { kitchenValidationSchema } from './kitchenValidation';
import { KitchenFormData } from '../../../app/data/models/kitchens/kitchenFormData';
import { useRestaurant } from '../../../app/hooks/useRestaurant';
import { LanguageFormTabs } from '../../../app/components/LanguageFormTabs';
import { LanguageCode } from '../../../app/data/models/common/localizedLanguage';
import { LocalizedLanguageFormData } from '../../../app/data/models/common/localizedLanguage';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import ErrorComponent from '../../../app/layout/ErrorComponent';
import { useNavigate, useParams } from 'react-router-dom';
import { toKitchenFormData } from '../../../app/data/models/kitchens/kitchenFormModel';
import { fetchKitchenDetailsAsync } from './kitchenDetailsSlice';
import { toast } from 'react-toastify';
import ListTitleBox from '../../../app/components/list/ListTitleBox';

const KitchenForm = () => {
  const { restaurantId, kitchenId } = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [kitchen, setKitchen] = useState<KitchenFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    restaurant,
    isLoading: isRestaurantLoading,
    error: restaurantError,
    retry: retryRestaurant,
  } = useRestaurant(restaurantId);
  const [activeTab, setActiveTab] = useState<number>(0);

  const defaultValues: KitchenFormData = {
    names: (restaurant?.languageCodes || []).reduce(
      (acc, code) => ({
        ...acc,
        [code]: '',
      }),
      {} as LocalizedLanguageFormData
    ),
  };

  const { control, handleSubmit, reset } = useForm<KitchenFormData>({
    mode: 'onTouched',
    resolver: yupResolver(
      kitchenValidationSchema(
        t,
        (restaurant?.languageCodes || []) as LanguageCode[]
      )
    ) as unknown as Resolver<KitchenFormData>,
    defaultValues,
  });

  const handleBack = useCallback(() => {
    navigate(`/manager/restaurants/${restaurantId}/kitchens`);
  }, [navigate, restaurantId]);

  const header = (
    <ListTitleBox
      title={
        kitchenId
          ? t('kitchen.form.edit.title')
          : t('kitchen.form.create.title')
      }
      onBackClick={handleBack}
    />
  );

  const fetchKitchenFormData = async (kitchenId: string) => {
    try {
      setIsLoading(true);
      const kitchenDetails = await dispatch(
        fetchKitchenDetailsAsync({ restaurantId, kitchenId })
      ).unwrap();
      const kitchenFormData = toKitchenFormData(kitchenDetails);
      setKitchen(kitchenFormData);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.title || t('common.error'));
      } else if (error instanceof Error) {
        toast.error(error.message || t('common.error'));
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (kitchenId) {
      fetchKitchenFormData(kitchenId);
    }
  }, [kitchenId, dispatch, restaurantId]);

  useEffect(() => {
    if (restaurant?.languageCodes) {
      reset(defaultValues);
    }
  }, [restaurant, reset]);

  useEffect(() => {
    if (kitchen && restaurant?.languageCodes) {
      reset(kitchen);
    }
  }, [kitchen, restaurant, reset]);

  const onSubmit = async (data: KitchenFormData) => {
    setIsSubmitting(true);
    try {
      if (kitchen && kitchen.id) {
        await dispatch(
          updateKitchenAsync({
            restaurantId,
            kitchenId: kitchen.id,
            kitchenDto: data,
          })
        );
      } else {
        await dispatch(createKitchenAsync({ restaurantId, kitchenDto: data }));
      }
      navigate(`/manager/restaurants/${restaurantId}/kitchens`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (errors: FieldErrors<KitchenFormData>) => {
    if (!restaurant?.languageCodes?.length) return;

    const nameErrors = errors.names;
    if (nameErrors) {
      const errorLanguageIndex = restaurant.languageCodes.findIndex(
        (code) => nameErrors[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
      }
    }
  };

  if (isRestaurantLoading) return <LoadingComponent header={header} />;
  if (restaurantError || !restaurant)
    return <ErrorComponent onPrimaryAction={retryRestaurant} header={header} />;

  if (isLoading && kitchenId) return <LoadingComponent header={header} />;

  if (kitchenId && !kitchen)
    return (
      <ErrorComponent
        header={header}
        title={t('errors.generic.title')}
        description={t('errors.generic.description')}
        primaryButtonText={t('common.retry')}
        secondaryButtonText={t('common.back')}
        onPrimaryAction={() => fetchKitchenFormData(kitchenId)}
      />
    );

  return (
    <>
      {header}
      <Box component={Paper} sx={{ width: '100%', mb: 3, p: 2 }}>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ mb: { xs: 2, sm: 3 } }}
          >
            <Grid item xs={12}>
              <LanguageFormTabs<KitchenFormData>
                languageCodes={restaurant.languageCodes}
                control={control}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                fields={[
                  {
                    name: 'names',
                    label: t('kitchen.form.fields.name'),
                  },
                ]}
              />
            </Grid>
          </Grid>
          <FormButtons
            loading={isSubmitting}
            submitText={kitchenId ? t('common.update') : t('common.create')}
          />
        </form>
      </Box>
    </>
  );
};

export default KitchenForm;
